import React from 'react';
import { ColumnsType, ColumnType } from 'antd/es/table';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckMarkIcon,
  CopyIcon,
  TrashIcon,
} from '@shared/ui/icons';

import { twMerge } from '@utils/twMerge';
import { copyToClipboard } from '@utils/copyToClipboard';

import {
  HeaderAttrs,
  OnHeaderCell,
  UploadDetailKind,
  UploadRow,
  UploadsTableConfigOptions,
} from './types';
import { isDetail, isSubheader } from './uploadHelpers';
import { defaultOpts } from './constants';
import {
  renderDateTime,
  renderNameEmail,
  renderStatus,
} from '../common/renderers';

const EMPTY_UID_LABEL = '\u2014';

const normalizeAssetUid = (uid: unknown): string => {
  if (uid == null) return '';

  if (typeof uid === 'string') return uid.trim();

  if (typeof uid === 'number' || typeof uid === 'boolean') {
    return String(uid).trim();
  }

  if (typeof uid !== 'object') return '';

  const maybeUid = uid as Record<string, unknown>;
  const uidKeys = [
    'dicomSeriesUid',
    'seriesInstanceUid',
    'seriesInstanceUID',
    'sopInstanceUid',
    'uid',
  ];

  for (const key of uidKeys) {
    const value = maybeUid[key];

    if (typeof value === 'string' || typeof value === 'number') {
      const normalized = String(value).trim();

      if (normalized) return normalized;
    }
  }

  return '';
};

const normalizeAssetDate = (date: unknown): string => {
  if (date == null) return '';

  if (typeof date === 'string') return date.trim();

  if (date instanceof Date) return date.toISOString();

  return '';
};

export function buildColumns(
  userOpts?: UploadsTableConfigOptions
): ColumnsType<UploadRow> {
  const opts = { ...defaultOpts, ...(userOpts ?? {}) };
  const {
    iconColWidth,
    detailIconIndentPx,
    detailContentIndentClass,
    headerBgClass,
    rowBgClass,
    headerBgInline,
    rowBgInline,
    headerTextClass,
    dataFontSizePx,
    dataLineHeightPx,
    actionIconSizeClass,
    dateTextClass,
    timeTextClass,
    onDeleteGroupClick,
    onViewExecutionRequest,
    onViewExecutionResponse,
  } = opts;

  const withHeader = (col: ColumnType<UploadRow>): ColumnType<UploadRow> => {
    const origOnHeaderCell = col.onHeaderCell;

    return {
      ...col,
      onHeaderCell: (...args: Parameters<OnHeaderCell>): HeaderAttrs => {
        const base: HeaderAttrs = origOnHeaderCell
          ? origOnHeaderCell(...args)
          : {};

        const style = base.style
          ? { ...base.style, background: headerBgInline }
          : { background: headerBgInline };

        return {
          ...base,
          className: twMerge(base.className ?? '', headerBgClass ?? ''),
          style,
        };
      },
    };
  };

  const dataTypographyInline: React.CSSProperties = {
    fontSize: `${dataFontSizePx}px`,
    lineHeight: `${dataLineHeightPx}px`,
  };

  const cellStyle = (
    row: UploadRow,
    extra?: React.CSSProperties
  ): React.CSSProperties => {
    let base: React.CSSProperties;

    if (isDetail(row)) {
      if (isSubheader(row)) {
        base = { background: headerBgInline };
      } else {
        base = { background: rowBgInline, ...dataTypographyInline };
      }
    } else {
      base = { background: rowBgInline, ...dataTypographyInline };
    }

    return extra ? { ...base, ...extra } : base;
  };

  const cellClass = (row: UploadRow) => {
    if (isDetail(row)) {
      return isSubheader(row) ? headerBgClass : rowBgClass || '';
    }
    return rowBgClass || '';
  };

  const renderAssetUid = (uid?: unknown): React.ReactNode => {
    const value = normalizeAssetUid(uid);
    const isEmpty = !value || value === '-' || value === EMPTY_UID_LABEL;

    if (isEmpty) {
      return (
        <span
          className='flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'
          title='—'
        >
          —
        </span>
      );
    }

    return (
      <div className='flex items-center gap-2 min-w-0 pr-3'>
        <span
          className='flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'
          title={value}
        >
          {value}
        </span>

        <button
          type='button'
          aria-label='Copy DICOM Series UID'
          className='shrink-0 text-[16px] leading-none cursor-pointer'
          onClick={() => copyToClipboard(value)}
        >
          <CopyIcon />
        </button>
      </div>
    );
  };

  const renderActionsCell = (
    row: UploadRow,
    actionIconSizeClass: string
  ): React.ReactNode => {
    if (!isDetail(row)) {
      return (
        <div className='w-full flex items-center justify-center gap-2'>
          <button
            type='button'
            aria-label='Delete upload group'
            className={twMerge('shrink-0', actionIconSizeClass)}
            onClick={e => {
              e.stopPropagation(); // don’t trigger row click
              onDeleteGroupClick?.(row);
            }}
          >
            <TrashIcon />
          </button>
        </div>
      );
    }

    if (isSubheader(row)) {
      return null;
    }

    if (row.detailKind === UploadDetailKind.EXEC) {
      const isRequestAvailable = row.requestData != null;
      const isResponseAvailable = row.responseData != null;
      const executionLabel = row.execLabel ?? 'execution';

      return (
        <div className='w-full flex items-center justify-center gap-2'>
          <button
            type='button'
            aria-label={`View execution request for ${executionLabel}`}
            disabled={!isRequestAvailable}
            className={twMerge(
              'shrink-0 disabled:opacity-40 disabled:cursor-not-allowed',
              actionIconSizeClass
            )}
            onClick={e => {
              e.stopPropagation();
              onViewExecutionRequest?.(row);
            }}
          >
            <ArrowUpIcon className='h-2 w-2 text-pastelBlue' />
          </button>
          <button
            type='button'
            aria-label={`View execution response for ${executionLabel}`}
            disabled={!isResponseAvailable}
            className={twMerge(
              'shrink-0 disabled:opacity-40 disabled:cursor-not-allowed',
              actionIconSizeClass
            )}
            onClick={e => {
              e.stopPropagation();
              onViewExecutionResponse?.(row);
            }}
          >
            <ArrowDownIcon className='h-2 w-2 text-pastelBlue' />
          </button>
        </div>
      );
    }

    return <div className='w-full flex items-center justify-center gap-2' />;
  };

  const getActionCellStyle = (
    row: UploadRow,
    cellStyle: (
      r: UploadRow,
      extra?: React.CSSProperties
    ) => React.CSSProperties
  ): React.CSSProperties => {
    const base = cellStyle(row);
    const needsTightPadding =
      !isDetail(row) || (isDetail(row) && !isSubheader(row));

    if (needsTightPadding) {
      return { ...base, paddingLeft: 0, paddingRight: 0 };
    }

    return base;
  };

  const renderUploadStatusCell = (
    row: UploadRow,
    headerTextClass?: string
  ): React.ReactNode => {
    if (!isDetail(row)) {
      return renderStatus(row.uploadStatus);
    }

    if (isSubheader(row)) {
      if (row.detailKind === UploadDetailKind.SUBHEADER_EXEC) {
        return (
          <div
            className={twMerge(
              'px-0 py-2 text-[14px] font-medium',
              headerTextClass
            )}
          >
            Upload Status
          </div>
        );
      }
      return null;
    }

    if (row.detailKind === UploadDetailKind.EXEC) {
      return renderStatus(row.detailUploadStatus);
    }

    return null;
  };

  const columns: ColumnsType<UploadRow> = [
    withHeader({
      title: '',
      key: '__icon',
      width: iconColWidth,
      render: (_: unknown, row: UploadRow) => {
        if (isDetail(row) && isSubheader(row)) return null;
        const detail = isDetail(row);
        return (
          <span
            className={[
              'inline-flex items-center w-full pl-2',
              detail ? 'justify-start' : 'justify-center',
            ].join(' ')}
          >
            <span
              className={twMerge(
                'inline-flex items-center text-xl mt-1 [&>svg]:text-yellowGreen'
              )}
            >
              <CheckMarkIcon />
            </span>
          </span>
        );
      },
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: isDetail(row)
          ? cellStyle(row, { paddingLeft: detailIconIndentPx })
          : cellStyle(row),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'Process',
      key: 'col1',
      width: 200,
      render: (_: unknown, row: UploadRow) => {
        if (!isDetail(row))
          return <span className='font-medium'>{row.processLabel}</span>;
        if (isSubheader(row)) {
          return (
            <div
              className={twMerge(
                'px-0 py-2 text-[14px] font-medium',
                headerTextClass
              )}
            >
              {row.detailKind === UploadDetailKind.SUBHEADER_EXEC
                ? 'Executions:'
                : 'Assets:'}
            </div>
          );
        }
        return (
          <div className={detailContentIndentClass}>
            {row.detailKind === UploadDetailKind.EXEC
              ? (row.execLabel ?? row.content)
              : (row.assetLabel ?? row.content)}
          </div>
        );
      },
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: cellStyle(row, {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'User',
      key: 'col2',
      width: 200,
      render: (_: unknown, row: UploadRow) => {
        if (!isDetail(row))
          return renderNameEmail(
            row.user,
            row.userEmail,
            dateTextClass,
            timeTextClass
          );
        if (isSubheader(row)) {
          if (row.detailKind === UploadDetailKind.SUBHEADER_EXEC) {
            return (
              <div
                className={twMerge(
                  'px-0 py-2 text-[14px] font-medium',
                  headerTextClass
                )}
              >
                Start Date
              </div>
            );
          }

          if (row.detailKind === UploadDetailKind.SUBHEADER_ASSETS) {
            return (
              <div
                className={twMerge(
                  'px-0 py-2 text-[14px] font-medium',
                  headerTextClass
                )}
              >
                Dicom Series UID
              </div>
            );
          }

          return null;
        }

        if (row.detailKind === UploadDetailKind.ASSETS) {
          return renderAssetUid(row.dicomSeriesUid);
        }

        return renderDateTime(row.startDate, dateTextClass, timeTextClass);
      },
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'Upload Date',
      key: 'col3',
      width: 130,
      render: (_: unknown, row: UploadRow) => {
        if (!isDetail(row)) {
          return renderDateTime(row.uploadDate, dateTextClass, timeTextClass);
        }

        if (isSubheader(row)) {
          const label =
            row.detailKind === UploadDetailKind.SUBHEADER_EXEC
              ? 'End Date'
              : 'Dicom Acquisition Date';
          return (
            <div
              className={twMerge(
                'px-0 py-2 text-[14px] font-medium',
                headerTextClass
              )}
            >
              {label}
            </div>
          );
        }

        if (row.detailKind === UploadDetailKind.EXEC) {
          return renderDateTime(row.endDate, dateTextClass, timeTextClass);
        }

        return renderDateTime(
          normalizeAssetDate(row.dicomAcquisitionDate),
          dateTextClass,
          timeTextClass
        );
      },
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'Preprocessing Status',
      key: 'col4',
      width: 150,
      render: (_: unknown, row: UploadRow) =>
        isDetail(row) ? null : renderStatus(row.preprocessingStatus),
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'Upload Status',
      key: 'col5',
      width: 150,
      render: (_: unknown, row: UploadRow) =>
        renderUploadStatusCell(row, headerTextClass),
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: cellStyle(row),
        colSpan: 1,
      }),
    }),

    withHeader({
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_: unknown, row: UploadRow) =>
        renderActionsCell(row, actionIconSizeClass),
      onCell: (row: UploadRow) => ({
        className: cellClass(row),
        style: getActionCellStyle(row, cellStyle),
        colSpan: 1,
      }),
    }),
  ];

  return columns;
}
